import React, { useContext, useState, useEffect, useMemo } from 'react';
import { Grid, TextField, Typography, Slider } from '@mui/material';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';

const ThemeOptions = ({
  formData,
  handleChange,
  setTempTheme = () => {
    console.log('no setTempTheme provided');
  },
}) => {
  const {
    state: {
      finalTheme,
      setPrimaryColor,
      setSecondaryColor,
      setTertiaryColor,
      setBorderWidth,
      setBorderRadius,
      setBorderColor,
      setBoxShadow,
    },
  } = useContext(ThemeContext);

  const [debouncedPrimaryColor, setDebouncedPrimaryColor] = useState(null);
  const [debouncedSecondaryColor, setDebouncedSecondaryColor] = useState(null);
  const [debouncedTertiaryColor, setDebouncedTertiaryColor] = useState(null);

  useEffect(() => {
    if (debouncedPrimaryColor !== null) {
      const handler = setTimeout(() => {
        setPrimaryColor(debouncedPrimaryColor);
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [debouncedPrimaryColor, setPrimaryColor]);

  useEffect(() => {
    if (debouncedSecondaryColor !== null) {
      const handler = setTimeout(() => {
        setSecondaryColor(debouncedSecondaryColor);
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [debouncedSecondaryColor, setSecondaryColor]);

  useEffect(() => {
    if (debouncedTertiaryColor !== null) {
      const handler = setTimeout(() => {
        setTertiaryColor(debouncedTertiaryColor);
      }, 1000);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [debouncedTertiaryColor, setTertiaryColor]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    // console.log(`Changing ${name} to ${value}`);
    handleChange(event);

    switch (name) {
      case 'primaryColor':
        setDebouncedPrimaryColor(value);
        break;
      case 'secondaryColor':
        setDebouncedSecondaryColor(value);
        break;
      case 'tertiaryColor':
        setDebouncedTertiaryColor(value);
        break;
      case 'borderColor':
        setBorderColor(value);
        break;
      case 'borderWidth':
        setBorderWidth(value);
        break;
      case 'borderRadius':
        setBorderRadius(value);
        break;
      case 'boxShadow':
        setBoxShadow(value);
        break;
      case 'iconOptions':
        // Handle icon options if needed
        break;
      default:
        break;
    }
  };

  const handleSliderChange = (name) => (event, value) => {
    // console.log(`Changing ${name} to ${value}`);
    handleChange({ target: { name, value } });

    switch (name) {
      case 'borderWidth':
        setBorderWidth(value);
        break;
      case 'borderRadius':
        setBorderRadius(value);
        break;
      case 'boxShadow':
        setBoxShadow(value);
        break;
      default:
        break;
    }
  };

  const dynamicStyles = useMemo(
    () => ({
      '& .MuiOutlinedInput-root': {
        borderRadius: finalTheme.palette.border.radius,
        '& fieldset': {
          borderColor: `${finalTheme.palette.border.color} !important`,
          borderWidth: `${finalTheme.palette.border.width}px !important`,
        },
        boxShadow: finalTheme.palette.boxShadow,
      },
    }),
    [finalTheme],
  );

  useEffect(() => {
    setTempTheme(dynamicStyles);
  }, [dynamicStyles, setTempTheme]);

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          sm={4}
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Primary Color"
            name="primaryColor"
            type="color"
            value={formData.primaryColor}
            onChange={handleInputChange}
            sx={dynamicStyles}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Secondary Color"
            name="secondaryColor"
            type="color"
            value={formData.secondaryColor}
            onChange={handleInputChange}
            sx={dynamicStyles}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Tertiary Color"
            name="tertiaryColor"
            type="color"
            value={formData.tertiaryColor}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={dynamicStyles}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <Typography gutterBottom>Border Width</Typography>
          <Slider
            value={formData.borderWidth}
            onChange={handleSliderChange('borderWidth')}
            min={0}
            max={10}
            step={1}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <Typography gutterBottom>Border Radius</Typography>
          <Slider
            value={formData.borderRadius}
            onChange={handleSliderChange('borderRadius')}
            min={0}
            max={12}
            step={1}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={4}
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Border Color"
            name="borderColor"
            type="color"
            value={formData.borderColor}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            sx={dynamicStyles}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <Typography gutterBottom>Box Shadow</Typography>
          <Slider
            value={formData.boxShadow}
            onChange={handleSliderChange('boxShadow')}
            min={0}
            max={24}
            step={1}
            valueLabelDisplay="auto"
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
        >
          <TextField
            fullWidth
            variant="outlined"
            label="Icon Options"
            name="iconOptions"
            value={formData.iconOptions}
            onChange={handleInputChange}
            sx={dynamicStyles}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default ThemeOptions;
